import { Link, Navigate, useParams } from 'react-router-dom'
import { Calendar, ArrowLeft } from 'lucide-react'
import { SEO, BreadcrumbStructuredData } from '@/components/seo/SEO'
import { getBlogBySlug } from '@/data/blogs'

function BlogContent({ blocks }) {
  if (!blocks || blocks.length === 0) return null

  return (
    <div className="prose prose-invert max-w-none prose-p:text-slate-300 prose-p:leading-relaxed prose-headings:text-white prose-headings:font-black prose-strong:text-white">
      {blocks.map((block, idx) => {
        if (!block || typeof block !== 'object') return null

        if (block.type === 'heading') {
          const level = block.level || 2
          const Tag = `h${Math.min(4, Math.max(2, level))}`
          return <Tag key={idx}>{block.text}</Tag>
        }

        if (block.type === 'paragraph') {
          return <p key={idx}>{block.text}</p>
        }

        if (block.type === 'quote') {
          return (
            <blockquote key={idx} className="border-l-4 border-game-primary/60 pl-4 text-slate-200">
              {block.text}
            </blockquote>
          )
        }

        if (block.type === 'list') {
          const items = Array.isArray(block.items) ? block.items : []
          return (
            <ul key={idx}>
              {items.map((item, itemIdx) => (
                <li key={itemIdx}>{item}</li>
              ))}
            </ul>
          )
        }

        return null
      })}
    </div>
  )
}

export function BlogPostPage() {
  const { slug } = useParams()
  const blog = getBlogBySlug(slug)

  if (!blog) {
    return <Navigate to="/blogs" replace />
  }

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Blogs', url: '/blogs' },
    { name: blog.title, url: `/blogs/${blog.slug}` },
  ]

  return (
    <>
      <SEO
        title={blog.title}
        description={blog.excerpt}
        image={blog.coverImage || undefined}
        url={`/blogs/${blog.slug}`}
        type="article"
      />
      <BreadcrumbStructuredData items={breadcrumbs} />

      <div className="min-h-screen bg-background-dark pt-32 pb-20">
        <div className="max-w-[900px] mx-auto px-4 lg:px-8">
          <Link to="/blogs" className="inline-flex items-center gap-2 text-slate-300 hover:text-white font-bold">
            <ArrowLeft className="w-4 h-4" />
            Back to Blogs
          </Link>

          <div className="mt-8">
            {blog.coverImage && (
              <div className="rounded-3xl overflow-hidden border border-border-dark bg-card-dark">
                <img
                  src={blog.coverImage}
                  alt={blog.title}
                  className="w-full aspect-[21/9] object-cover"
                />
              </div>
            )}

            <div className="mt-8">
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                {blog.publishedAt && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-game-primary" />
                    {new Date(blog.publishedAt).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                )}
                {blog.author && <span>• {blog.author}</span>}
                {blog.readingTime && <span>• {blog.readingTime}</span>}
              </div>

              <h1 className="text-white text-3xl md:text-5xl font-black tracking-tight mt-3 leading-tight">
                {blog.title}
              </h1>

              {blog.excerpt && (
                <p className="text-slate-300 mt-4 text-lg leading-relaxed">
                  {blog.excerpt}
                </p>
              )}

              {blog.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-6">
                  {blog.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-black text-slate-200 uppercase"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="h-px bg-border-dark my-10" />

              <BlogContent blocks={blog.content} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
